import React from "react";
import { useAuth } from "react-oidc-context";
import { BaseMessageType } from "../../../../types/MessageType";
import { ClientReportType } from "../../../../types/ClientReportType";
import { ClientReportStatusRecipients } from "../types";
import { useReportControls } from "../hooks/ReportControlContext";
import { toNString } from "../../../../helpers/guids";

const ReportMessageContext = React.createContext<{
    clearMessage: () => void,
    createMessage: (threadId: string, statusRecord: ClientReportStatusRecipients, newAuthorId?: string | null) => void,
    message: BaseMessageType | null,
    updateMessage: (message: string) => void,
    updateRecipients: (recipients: string[]) => void
}>({
    clearMessage: () => {},
    createMessage: () => {},
    message: null,
    updateMessage: () => {},
    updateRecipients: () => {}
});

export const useReportMessage = () => React.useContext(ReportMessageContext);

export const ReportMessageProvider = ({
    children,
    data = null
} : Readonly<{
    children: React.ReactNode
    data: ClientReportType | null
}>) => {
    const { user } = useAuth();
    const { reportStatuses } = useReportControls(); 
    const [message, setMessage] = React.useState<BaseMessageType | null>(null);

    const createMessage = (threadId: string, statusRecord: ClientReportStatusRecipients, newAuthorId?: string | null) : void => {
        const { authorId, status } = data!;
        const { defaultRecipients, isAuthorNotified, selector } = statusRecord;
        const newRecipients = isAuthorNotified ? [...defaultRecipients, newAuthorId ?? authorId] : defaultRecipients;

        setMessage((draft) => {
            if (draft == null || newAuthorId == null) {
                return {
                    threadId,
                    text: '',
                    recipients: newRecipients.filter(id => id !== toNString(user!.profile.sub))
                } as BaseMessageType;
            }

            // Rebuild the recipients
            const { isAuthorNotified, defaultRecipients } = reportStatuses.find(el => el.data!.selector === status)?.data ?? { isAuthorNotified: false, defaultRecipients: [] };
            const oldDefaultRecipients = isAuthorNotified ? [...defaultRecipients, authorId] : defaultRecipients;

            return {
                ...draft,
                threadId,
                // Recipients should be the new status's list with any user-added (non-default + author, if relevant) users from the old list
                recipients: [
                    ...draft.recipients.filter(el => !oldDefaultRecipients.includes(el)),
                    ...newRecipients
                ]
            } as BaseMessageType;
        });
    };

    const updateMessage = (message: string) => setMessage((draft) => {
        if (!draft) {
            return null;
        }
        return {
            ...draft,
            text: message
        } as BaseMessageType;
    });

    const updateRecipients = (recipients: string[]) => setMessage((draft) => {
        if (!draft) {
            return null;
        }
        return {
            ...draft,
            recipients
        } as BaseMessageType;
    });

    return (
        <ReportMessageContext.Provider value={{
            clearMessage: () => setMessage(() => null),
            createMessage,
            message,
            updateMessage,
            updateRecipients
        }}>
            {children}
        </ReportMessageContext.Provider>
    );
};